import React, { FC, ReactElement, useState } from 'react'
import { TelSysConfiguration } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { DocTopic } from '../../Common/Constants/Documentation'
import {
    LanguageContextProvider,
    MessageContextProvider,
    PromptContextProvider,
    UserInfoContextProvider
} from '../../Common/Contexts'
import { CommonFormContextProvider } from '../../Common/Contexts/CommonFormContext'
import { ReactState } from '../../Common/Types/Types'
import { clearModalHelpTopic, setModalHelpTopicID } from '../../Common/Utils/HelpPortal'
import { TelSysTabs } from '../Main/TelSysTabs'
import { Modal } from '../../../ReactRouting/Components/Molecules/Modal/Modal'
import { $window } from '../../../NonReact/common/AkixiWindow'

type Props = {
    /** Invoked after successful saves so parent lists can refetch. */
    refreshAction: () => void
    /** CME scope applied when loading TelSys data inside the modal. */
    filterCMEnvironmentID: string
    /** When set, opens change/view flow for the given TelSys id; omit for add flow. */
    selectedTelsys?: string
    /** When true, fields are read-only (view mode). */
    viewOnly?: boolean
    /** Called after the modal finishes closing. */
    onModalExited?: () => void
}

export const TelSysConfigurationModal: FC<Props> = ({
    refreshAction,
    filterCMEnvironmentID,
    selectedTelsys,
    viewOnly,
    onModalExited
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()

    language.loadBundle(TelSysConfiguration.NAME)

    // Whether the modal window is open.
    const [isModalOpen, setModalOpen]: ReactState<boolean> = useState<boolean>(true)

    const closeModal = (): void => {
        $window.disableHotKeys = false
        // Component is controlled externally.
        setModalOpen(false)
    }

    return (
        <div>
            <CommonFormContextProvider>
                <Modal
                    isOpen={isModalOpen}
                    isViewOnly={viewOnly}
                    closeOnBackdropClick={true}
                    isClosableWithXButton={true}
                    inlineExitPromptSettings={{
                        message: language.get(TelSysConfiguration.TelSysConfiguration_PromptMessage),
                        confirmButtonText: language.get(TelSysConfiguration.TelSysConfiguration_PromptConfirm),
                        cancelButtonText: language.get(TelSysConfiguration.TelSysConfiguration_PromptCancel),
                        isRouterMode: true
                    }}
                    onOpen={(): void => {
                        // Add contextual doc topic ID to the window object which is used
                        // in old JSP logic that handles F1 key press.
                        setModalHelpTopicID(DocTopic.ADMINTELSERVERS)
                    }}
                    onClose={closeModal}
                    onExited={(): void => {
                        // Remove the contextual doc topic ID from the window that is used
                        // in old JSP logic that handles F1 key press.
                        // When removed the previous logic will work and the modal window doc topic will be not considered.
                        clearModalHelpTopic()
                        onModalExited?.()
                    }}
                >
                    {isModalOpen ? (
                        <LanguageContextProvider>
                            <PromptContextProvider>
                                <MessageContextProvider>
                                    <UserInfoContextProvider>
                                        <div className='modal-title-report-config'>
                                            {selectedTelsys
                                                ? language.get(TelSysConfiguration.TelSysConfiguration_ChangeTitle)
                                                : language.get(TelSysConfiguration.TelSysConfiguration_AddTitle)}
                                        </div>
                                        <div className='telsys-configuration'>
                                            <TelSysTabs
                                                viewOnly={viewOnly}
                                                refreshAction={refreshAction}
                                                closeModal={closeModal}
                                                filterCMEnvironmentID={filterCMEnvironmentID}
                                                selectedTelsys={selectedTelsys}
                                            />
                                        </div>
                                    </UserInfoContextProvider>
                                </MessageContextProvider>
                            </PromptContextProvider>
                        </LanguageContextProvider>
                    ) : (
                        <> </>
                    )}
                </Modal>
            </CommonFormContextProvider>
        </div>
    )
}
